import { request } from './http'

export interface GameplayVector {
  core_loop?: string
  agency?: string
  info_asymmetry?: string
  social_frame?: string
  reward_schedule?: string
  payoff_emotion?: string
  tempo?: string
  rule_clarity?: string
  core_loop_weight?: number
  agency_weight?: number
  info_weight?: number
  social_weight?: number
  reward_weight?: number
  payoff_weight?: number
  tempo_weight?: number
  rule_clarity_weight?: number
  custom_instruction?: string
  forbidden_mix?: string
}

export interface GameplayProfile {
  project_id: string
  version: number
  template_key: string
  target_vector: GameplayVector
  cadence_json?: string
  forbidden_mix_json?: string
}

export interface GameplayTemplate {
  template_key: string
  name: string
  default_vector: GameplayVector
  core_loop_desc: string
  reader_appetite: string
}

export interface ChapterGameplayState {
  project_id: string
  branch_id?: string
  chapter_no: number
  target_vector: GameplayVector
  injected_context?: string
  observed_vector?: GameplayVector
  gameplay_score?: number
  qc_detail_json?: string
  status: string
}

export interface GameplayTrajectoryPoint {
  chapter_no: number
  target_core: number
  observed_core: number
  target_payoff: number
  observed_payoff: number
  score: number
  status: string
  deviation: number
}

export interface PutGameplayProfileInput {
  version?: number
  template_key?: string
  target_vector?: GameplayVector
  cadence_json?: string
  forbidden_mix_json?: string
}

export interface PutChapterTargetInput {
  target_vector: GameplayVector
}

interface GameplayTemplatesResponse {
  templates: GameplayTemplate[] | null
}

interface GameplayTrajectoryResponse {
  project_id: string
  trajectory: GameplayTrajectoryPoint[] | null
}

function branchQuery(branchId?: string) {
  if (!branchId) return ''
  return `?${new URLSearchParams({ branch_id: branchId }).toString()}`
}

export const gameplayApi = {
  listTemplates: async (): Promise<GameplayTemplate[]> => {
    const data = await request<GameplayTemplatesResponse>('/v1/gameplay/templates')
    return data.templates ?? []
  },

  getProfile: (projectId: string): Promise<GameplayProfile> =>
    request<GameplayProfile>(`/v1/projects/${encodeURIComponent(projectId)}/gameplay-profile`),

  putProfile: (projectId: string, body: PutGameplayProfileInput): Promise<GameplayProfile> =>
    request<GameplayProfile>(`/v1/projects/${encodeURIComponent(projectId)}/gameplay-profile`, {
      method: 'PUT',
      body,
    }),

  getChapterGameplay: (projectId: string, chapterNo: number, branchId?: string): Promise<ChapterGameplayState> =>
    request<ChapterGameplayState>(
      `/v1/projects/${encodeURIComponent(projectId)}/chapters/${chapterNo}/gameplay${branchQuery(branchId)}`,
    ),

  putChapterTarget: (
    projectId: string,
    chapterNo: number,
    body: PutChapterTargetInput,
    branchId?: string,
  ): Promise<ChapterGameplayState> =>
    request<ChapterGameplayState>(
      `/v1/projects/${encodeURIComponent(projectId)}/chapters/${chapterNo}/gameplay/target${branchQuery(branchId)}`,
      { method: 'PUT', body },
    ),

  getTrajectory: async (projectId: string, branchId?: string): Promise<GameplayTrajectoryResponse> =>
    request<GameplayTrajectoryResponse>(
      `/v1/dashboard/project/${encodeURIComponent(projectId)}/gameplay_trajectory${branchQuery(branchId)}`,
    ),
}
